import React, {useCallback, useEffect, useRef} from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useDataTable from "../../../hooks/useDataTable";
import {useAuth} from "../../../hooks/useAuth";
import CustomDataTable from "../../../shared/tables/CustomDataTable";
import GenericButton from "../../../shared/ui/GenericButton";
import StyledLink from "../../../shared/ui/LinkStyled";
import {getRomanNumber} from "../../../utils/functions";
import {APIDeleteReport, APIGetReports, APIGetReportsClassrooms} from "../schoolReport/api/schoolReports";

type Report = {
    id: number;
    tag: any[];
    classroom_name: string;
    classroom_id: number;
    classroom_grade: number;
    start_date: string;
    end_date: string;
    subject: string;
    created_at: string;
}
const SCHOOL_REPORTS_COLUMNS = [
    {id: "subject", label: "Disciplină",nowrap: true,},
    {id: "interval", label: "Perioada", nowrap: true,},
    {id: "created_at", label: "Creat la", nowrap: true,},
    {id: "tag", label: "Tag", noWrap: true, tooltip: true, width: "200px", tooltipTextLength: 21},
    {id: "actions", label: "Acțiuni", numeric: true, sticky: true,}
];

const CLASSROOMS_REPORTS_COLUMNS = [
    {id: "classroom_name", label: "Clasa", noWrap: true,},
    {id: "subject", label: "Disciplină", nowrap: true,},
    {id: "interval", label: "Perioada", nowrap: true,},
    {id: "created_at", label: "Creat la", nowrap: true,},
    {id: "tag", label: "Tag", noWrap: true, tooltip: true, width: "200px", tooltipTextLength: 21},
    {id: "actions", label: "Acțiuni", numeric: true, sticky: true,}
]

const ReportsList = ({type, classroomId = ""}: { type: string, classroomId: string }) => {
    const {update} = useAuth();

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        page,
        perPage,
        setPerPage,
        totalRows,
        setTotalRows,
        handleChangePage,
    } = useDataTable("", "", undefined, "");

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const mapReportsRows = useCallback((report: Report) => {

        const isClassrooms = type === "classroom";

        const reportTag = report.tag.length === 0 ? "-" : report.tag.map((item: string, index: number) => (
            <Box key={index} mr={1} display="inline">{item}</Box>
        ));

        const classroomLink = report.classroom_name && (
            <StyledLink to={`/clase/${report.classroom_id}`}>
                ({getRomanNumber(report.classroom_grade)}) {report.classroom_name}
            </StyledLink>
        );

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box mr={1}>
                    <GenericButton
                        type="report"
                        onClick={() => {
                            if (type === "school") {
                                window.open(`/rapoarte-scoala/${report.id}`);
                            } else if (type === "classroom" || type === "classrooms") {
                                window.open(`/rapoarte-clasa/${report.id}`);
                            }
                        }}
                    />
                </Box>
                <Box>
                    <GenericButton type="delete" onClick={() => confirmDelete(report)}/>
                </Box>
            </Box>
        );

        return {
            ...(isClassrooms && {classroom_name: classroomLink}),
            subject: report.subject,
            interval: `${moment(report.start_date).locale("ro").format("DD.MM.YYYY")} - ${moment(report.end_date).locale("ro").format("DD.MM.YYYY")}`,
            created_at: moment(report.created_at).locale("ro").format("HH:mm, DD.MM.YYYY"),
            tag: reportTag,
            actions: actions,
        }
    }, [type]);

    const getReports = useCallback((currentPage = pageRef.current) => {
        if(type === "classrooms") {
            APIGetReportsClassrooms({page: `?page=${currentPage + 1}`})
                .then((response) => {
                    if (response.data.success) {
                        // console.log("APIGetReports OK", response);
                        setTableData(response.data.data.data.map((data: any) => mapReportsRows(data)));
                        setTotalRows(response.data.data.total);
                        setPerPage(response.data.data.per_page);
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch((err) => {
                    // console.log("getClassroom ERR", err);
                    update("error", {show: true, code: "A001"});
                })
                .finally(() => setLoading(false))
        } else {
            APIGetReports({page: `?type=${type}&page=${currentPage + 1}&type_id=${classroomId}`})
                .then((response) => {
                    if (response.data.success) {
                        // console.log("APIGetReports OK", response);
                        setTableData(response.data.data.data.map((data: any) => mapReportsRows(data)));
                        setTotalRows(response.data.data.total);
                        setPerPage(response.data.data.per_page);
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch((err) => {
                    // console.log("getClassroom ERR", err);
                    update("error", {show: true, code: "A001"});
                })
                .finally(() => setLoading(false))
        }
    }, [type, setTableData, setTotalRows, setPerPage, update, setLoading, classroomId, mapReportsRows]);

    useEffect(() => {
        setLoading(true);
        getReports();
    }, [page]);

    const deleteReport = (id: string, callback: () => void) => {
        update("loading", {show: true, modal: true});

        APIDeleteReport({id})
            .then((response) => {
                if (response.data.success) {
                    // console.log("APIDeleteReport OK", response);
                    getReports(pageRef.current);
                    setTotalRows(prevState => prevState - 1);
                } else {
                    update("error", {show: true, code: "A002"});
                }
                callback();
            })
            .catch((err) => {
                // console.log("APIDeleteReport ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => {
                update("loading", {show: false});
            });
    };

    const confirmDelete = (report: {
        id: number;
        [key: string]: any;
    }) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat trash-can",
            title: "Ești sigur că vrei să ștergi acest raport?",
            message: (
                <>
                    Raportul de <strong> {report.subject}, {report.tag}</strong> creat în data de
                    <strong>{moment(report.created_at).locale("ro").format("DD.MM.YYYY")}</strong> va fi șters
                    definitiv!
                </>
            ),
            returnFunction: (callback: () => void) => {
                deleteReport(report.id.toString(), callback);
            },
            buttonYes: "Șterge",
            buttonNo: "Închide",
        });
    };

    return (
        <>
            <Typography variant="body2" sx={{marginBottom: 1,}}>*Rapoartele vor putea fi vizualizate mai jos în maximum
                5 minute după generare. Dacă acestea nu apar în intervalul de timp precizat, vă rugăm să reîmprospătați
                pagina.
            </Typography>
            <CustomDataTable
                loading={loading}
                columns={type === "classrooms" ? CLASSROOMS_REPORTS_COLUMNS : SCHOOL_REPORTS_COLUMNS}
                rows={tableData}
                rowsPerPage={perPage}
                page={page}
                totalRows={totalRows}
                onPageChange={handleChangePage}
                hasSearch={false}
            />
        </>
    )
}

export default ReportsList;